import { useCallback } from 'react';
import { FixtureData } from '@graphql/generated/graphql';
import { ZoneCreationProperties } from '../types/zoneMapType';

/**
 * Modifier keys state for multi-select operations
 */
export interface ModifierKeys {
  shift: boolean;
  ctrl: boolean;
  meta: boolean;
}

/**
 * Props for useFixtureSelection hook
 */
export interface UseFixtureSelectionProps {
  /** Map of all zones being created/edited */
  zoneCreationProperties: Map<number, ZoneCreationProperties>;
  /** Setter for zone creation properties */
  setZoneCreationProperties: React.Dispatch<React.SetStateAction<Map<number, ZoneCreationProperties>>>;
  /** Global list of all selected fixtures across all zones */
  selectedFixtures: string[];
  /** Setter for selected fixtures */
  setSelectedFixtures: React.Dispatch<React.SetStateAction<string[]>>;
  /** List of all available agile fixtures from the floorplan */
  agileFixtures: FixtureData[];
  /**
   * Index of the currently active zone that will receive fixture selections.
   * This is managed by the parent component (ZoneViewer) and determines which
   * zone fixtures are assigned to when selected on the floorplan.
   *
   * Users can change the active zone by:
   * - Clicking on a zone card in the sidebar
   * - Using the zone selector dropdown in the toolbar
   * - Creating a new zone (automatically becomes active)
   */
  activeZoneIdx: number;
}

/**
 * Selection result for providing feedback
 */
export interface SelectionResult {
  success: boolean;
  selectedCount: number;
  filteredCount: number;
  error?: string;
}

/**
 * Return type for useFixtureSelection hook
 */
export interface UseFixtureSelectionReturn {
  /** Click a fixture to add/remove it from the active zone. */
  handleFixtureClick: (fixtureId: string, modifierKeys: ModifierKeys) => SelectionResult | null;
  /** Assign a batch of fixtures (Shift+drag marquee) directly to the active zone. */
  assignFixturesToActiveZone: (fixtureIds: string[]) => SelectionResult;
  getFixtureAssignedZone: (fixtureId: string) => string | null;
}

/**
 * Custom hook to manage fixture selection for zone creation.
 *
 * Provides single-click toggle assignment and batch (marquee) assignment of
 * fixtures to the active zone, plus availability validation.
 *
 * ## Active Zone Concept
 * The "active zone" is the zone that will receive any fixtures selected on the
 * floorplan. Only one zone can be active at a time. The parent component
 * (ZoneViewer) manages which zone is active and passes the activeZoneIdx here.
 *
 * @param props - Configuration for fixture selection
 * @returns Selection operations
 */
export const useFixtureSelection = (props: UseFixtureSelectionProps): UseFixtureSelectionReturn => {
  const { zoneCreationProperties, setZoneCreationProperties, setSelectedFixtures, agileFixtures, activeZoneIdx } =
    props;

  /**
   * Get the zone name that a fixture is assigned to (if any)
   * Returns null if fixture is not assigned to any other zone
   */
  const getFixtureAssignedZone = useCallback(
    (fixtureId: string): string | null => {
      for (const [zoneIdx, zone] of zoneCreationProperties.entries()) {
        if (zone.zoneIdx !== activeZoneIdx && zone.fixtureIds.includes(fixtureId)) {
          return zone.zoneName || `Zone ${zoneIdx + 1}`;
        }
      }
      return null;
    },
    [zoneCreationProperties, activeZoneIdx],
  );

  /**
   * Check if a fixture is available for selection
   * A fixture is available if:
   * 1. It exists in the agileFixtures list
   * 2. It's not already assigned to another zone
   * 3. Or it's already assigned to the current active zone
   */
  const isFixtureAvailable = useCallback(
    (fixtureId: string): boolean => {
      // Check if fixture exists in agile fixtures
      const fixtureExists = agileFixtures.some((fixture) => fixture.id === fixtureId);
      if (!fixtureExists) {
        return false;
      }

      // Check if fixture is already assigned to another zone
      const currentZone = zoneCreationProperties.get(activeZoneIdx);
      if (!currentZone) {
        return false;
      }

      // If fixture is in current zone, it's available
      if (currentZone.fixtureIds.includes(fixtureId)) {
        return true;
      }

      // Check if fixture is assigned to any other zone
      return getFixtureAssignedZone(fixtureId) === null;
    },
    [agileFixtures, zoneCreationProperties, activeZoneIdx, getFixtureAssignedZone],
  );

  /**
   * Handle single fixture click selection
   * Simple toggle behavior - click to add/remove fixture from active zone
   */
  const handleFixtureClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (fixtureId: string, _modifierKeys: ModifierKeys): SelectionResult | null => {
      const currentZone = zoneCreationProperties.get(activeZoneIdx);
      if (!currentZone) {
        console.error(`Active zone ${activeZoneIdx} not found`);
        return {
          success: false,
          selectedCount: 0,
          filteredCount: 0,
          error: 'Active zone not found',
        };
      }

      const isCurrentlySelected = currentZone.fixtureIds.includes(fixtureId);

      // If removing, always allow it
      if (isCurrentlySelected) {
        const newFixtureIds = currentZone.fixtureIds.filter((id) => id !== fixtureId);

        setZoneCreationProperties((prev) => {
          const newMap = new Map(prev);
          newMap.set(activeZoneIdx, {
            ...currentZone,
            fixtureIds: newFixtureIds,
          });
          return newMap;
        });

        setSelectedFixtures((prev) => prev.filter((id) => id !== fixtureId));

        return null; // No feedback needed for removal
      }

      // Validate fixture availability for adding
      if (!isFixtureAvailable(fixtureId)) {
        const assignedZone = getFixtureAssignedZone(fixtureId);
        return {
          success: false,
          selectedCount: 0,
          filteredCount: 1,
          error: assignedZone ? `Fixture is already assigned to ${assignedZone}` : 'Fixture is not available',
        };
      }

      // Add fixture
      const newFixtureIds = [...currentZone.fixtureIds, fixtureId];

      setZoneCreationProperties((prev) => {
        const newMap = new Map(prev);
        newMap.set(activeZoneIdx, {
          ...currentZone,
          fixtureIds: newFixtureIds,
        });
        return newMap;
      });

      setSelectedFixtures((prev) => [...prev, fixtureId]);

      return null; // No feedback needed for single selection
    },
    [
      isFixtureAvailable,
      getFixtureAssignedZone,
      zoneCreationProperties,
      activeZoneIdx,
      setZoneCreationProperties,
      setSelectedFixtures,
    ],
  );

  /**
   * Assign a batch of fixtures (Shift+drag marquee) directly to the active zone.
   * Fixtures already in the active zone, or assigned to another zone, are
   * skipped (counted as filtered) — mirroring single-click availability rules.
   */
  const assignFixturesToActiveZone = useCallback(
    (fixtureIds: string[]): SelectionResult => {
      const currentZone = zoneCreationProperties.get(activeZoneIdx);
      if (!currentZone) {
        return { success: false, selectedCount: 0, filteredCount: 0, error: 'Active zone not found' };
      }

      const alreadyInZone = new Set(currentZone.fixtureIds);
      const toAdd = fixtureIds.filter((id) => !alreadyInZone.has(id) && isFixtureAvailable(id));
      const filteredCount = fixtureIds.length - toAdd.length;

      if (toAdd.length === 0) {
        return {
          success: false,
          selectedCount: 0,
          filteredCount,
          error: 'All selected fixtures are already assigned',
        };
      }

      const newFixtureIds = [...currentZone.fixtureIds, ...toAdd];
      setZoneCreationProperties((prev) => {
        const newMap = new Map(prev);
        newMap.set(activeZoneIdx, { ...currentZone, fixtureIds: newFixtureIds });
        return newMap;
      });
      setSelectedFixtures((prev) => [...prev, ...toAdd]);

      return { success: true, selectedCount: toAdd.length, filteredCount };
    },
    [zoneCreationProperties, activeZoneIdx, isFixtureAvailable, setZoneCreationProperties, setSelectedFixtures],
  );

  return {
    handleFixtureClick,
    assignFixturesToActiveZone,
    getFixtureAssignedZone,
  };
};
